import React, { createContext, useContext, useMemo, useState } from 'react';
import { USER_NOTIFICATION_TYPES, type UserNotificationType } from '../constants/userNotificationTypes';

export type NoticeAction = {
  label: string;
  href?: string;
  onClick?: () => void;
  variant?: 'solid' | 'outline' | 'ghost';
  colorScheme?: string;
};

export type Notice = {
  id: string;
  type: UserNotificationType; // ドキュメント準拠: info|success|warning|error
  title: string;
  description?: string;
  actions?: NoticeAction[];
  priority?: number; // 小さいほど優先
  onceKey?: string; // 表示/非表示を localStorage で保持するキー
};

type NoticeContextValue = {
  notices: Notice[];
  addNotice: (n: Notice) => void;
  removeNotice: (id: string) => void;
  dismissNotice: (id: string) => void;
  clearNotices: () => void;
};

const NoticeContext = createContext<NoticeContextValue | undefined>(undefined);

export const NoticeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notices, setNotices] = useState<Notice[]>([]);

  const addNotice = (n: Notice) => {
    setNotices((prev) => {
      // onceKey があり、既に dismissed 済みなら追加しない
      if (n.onceKey && typeof window !== 'undefined') {
        const dismissed = localStorage.getItem(`notice:dismissed:${n.onceKey}`);
        if (dismissed === '1') return prev;
      }
      // 重複 ID を弾く
      if (prev.some((p) => p.id === n.id)) return prev;
      return [...prev, n].sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100));
    });
  };

  const removeNotice = (id: string) => setNotices((prev) => prev.filter((n) => n.id !== id));

  const dismissNotice = (id: string) => {
    setNotices((prev) => {
      const notice = prev.find((n) => n.id === id);
      if (notice?.onceKey && typeof window !== 'undefined') {
        localStorage.setItem(`notice:dismissed:${notice.onceKey}`, '1');
      }
      return prev.filter((n) => n.id !== id);
    });
  };

  const clearNotices = () => setNotices([]);

  // 注: 従業員追加のオンボーディング通知は組織管理画面（/admin/organization）で専用カードとして表示するため、
  // グローバル通知としては表示しない（2025-01-20 変更）

  const value = useMemo(
    () => ({ notices, addNotice, removeNotice, dismissNotice, clearNotices }),
    [notices]
  );

  return <NoticeContext.Provider value={value}>{children}</NoticeContext.Provider>;
};

export const useNotices = () => {
  const ctx = useContext(NoticeContext);
  if (!ctx) throw new Error('useNotices must be used within NoticeProvider');
  return ctx;
};


